import { Datatype } from '@framework/engine/schema/expressions/datatype/Datatype.ts';
import { evaluateExpression } from '@framework/engine/schema/expressions/evaluate/evaluateExpression.ts';
import { mapReferenceToPath } from '@framework/engine/schema/expressions/evaluate/mapReferenceToPath.ts';
import { createEvaluationVariables } from '@framework/engine/schema/expressions/evaluate/variables/IEvaluationVariables.ts';
import { ExpressionType } from '@framework/engine/schema/expressions/IExpression.ts';
import { collectExpressionReferences } from '@framework/engine/schema/expressions/references/collectExpressionReferences.ts';
import { type IReference } from '@framework/engine/schema/expressions/references/IReference.ts';
import { type IReferenceExpression } from '@framework/engine/schema/expressions/repository/reference/IReferenceExpression.ts';
import { FormBuilderMetaFlags } from '@framework/engine/validator/form-builder/flags/FormBuilderMetaFlags.ts';
import { parseAdvancedRichTextToString } from '@framework/engine/validator/utils/parseAdvancedRichTextToString.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { yup } from '@framework/validation/initYup.ts';
import { type Maybe } from 'yup';
import { ExportedMetadataSiblingPathsMetaKey } from '../../directives/metadata/IMetadataDirective.ts';
import { type IUploadWidget } from './IUploadWidget.ts';
import {
  getContextFileNameField,
  publicDataUploadWidgetName,
} from './UploadWidget.type.tsx';

export function uploadWidgetFilenameField(code: string) {
  return `${code}@filename`;
}

export function createUplaodWidgetFormSchema(input: { widget: IUploadWidget }) {
  const { widget } = input;

  const publicUpload = widget.directives?.omissis?.enabled ?? false;
  const widgetName = widget.code;
  const filenameField = uploadWidgetFilenameField(widgetName);
  // Se gli omissis sono attivi, aggiungo un campo per salvare i dati del file "pubblico"
  const publicWidgetName = publicDataUploadWidgetName(widgetName);
  const publicFilenameField = uploadWidgetFilenameField(publicWidgetName);

  // ID di default da reference
  const defaultIdReferenceData = getDefaultIdReferenceData(widget);

  // Nome file di default da reference
  const defaultFilenameReferenceData = getDefaultFilenameReferenceData(widget);

  let publicLabelText: string | null = null;
  if (widget.publicUploadLabel) {
    const publicLabel = widget.publicUploadLabel ?? null;
    publicLabelText = parseAdvancedRichTextToString(publicLabel);
  }

  const publicUploadSchema = {
    [publicWidgetName]: yup
      .string()
      .notRequired()
      .typeError(
        t(
          'upload-widget.file-must-be-uploaded',
          'Il file ${label} deve essere caricato',
        ),
      )
      .label(
        publicLabelText ?? t('upload-widget.public-doc', 'Documento pubblico'),
      )
      // Questo campo è sempre opzionale
      .formBuilderFlags(
        FormBuilderMetaFlags.IgnoreRequired,
        FormBuilderMetaFlags.SkipExportMetadata,
      ),
    [publicFilenameField]: yup
      .string()
      .notRequired()
      .formBuilderFlags(
        FormBuilderMetaFlags.SkipDefaultValue,
        FormBuilderMetaFlags.SkipValidationRules,
      )
      .when([publicWidgetName ?? '<Not-Defined>'], ([code], schema: any) => {
        if (!code) {
          // Se non c'è un file selezionato, dobbiamo rimuovere il valore
          // del nome del file.
          return schema.transform(() => null);
        }
        return schema;
      }),
  };

  const result = {
    [widgetName]: yup
      .string()
      .notRequired()
      .typeError(
        t(
          'upload-widget.file-must-be-uploaded',
          'Il file ${label} deve essere caricato',
        ),
      )
      .meta({
        [ExportedMetadataSiblingPathsMetaKey]: [
          filenameField,
          ...(publicUpload ? [publicWidgetName, publicFilenameField] : []),
        ],
      }),
    [filenameField]: yup
      .string()
      .notRequired()
      .formBuilderFlags(
        FormBuilderMetaFlags.SkipDefaultValue,
        FormBuilderMetaFlags.SkipValidationRules,
      )
      .when([widgetName ?? '<Not-Defined>'], ([code], schema: any) => {
        if (!code) {
          // Se non c'è un file selezionato, dobbiamo rimuovere il valore
          // del nome del file.
          return schema.transform(() => null);
        }
        return schema;
      }),
    ...(publicUpload ? publicUploadSchema : {}),
  };

  // Andiamo a impostare i valori di default da reference se presenti
  result[widgetName] = getDefaultParentWhen(
    result[widgetName],
    defaultIdReferenceData,
  );
  result[filenameField] = getDefaultParentWhen(
    result[filenameField],
    defaultFilenameReferenceData,
  );

  return result;
}

/**
 * Costruisce i dati necessari per calcolare il valore di default (id del documento).
 * Per gli upload widget il default è impostabile solo come reference.
 * e.g. documento di procura
 */
function getDefaultIdReferenceData(widget: IUploadWidget) {
  if (!widget.defaultValueReference) return null;

  const expression: IReferenceExpression = {
    type: ExpressionType.Reference,
    value: widget.defaultValueReference,
  };
  const references = collectExpressionReferences(expression);
  const paths = references.map(r => mapReferenceToPath(r));

  return {
    paths,
    expression,
    references,
  };
}

/**
 * Costruisce i dati necessari per calcolare il valore di default del nome del file.
 * Per gli upload widget il default è impostabile solo come reference.
 * e.g. documento di procura
 *
 * Il campo a cui fare reference non è presente in configurazinoe ma viene
 * costruito dinamicamente.
 */
function getDefaultFilenameReferenceData(widget: IUploadWidget) {
  if (!widget.defaultValueReference) return null;

  const filenameReference: IReference = {
    ...widget.defaultValueReference,
    code: getContextFileNameField(widget.defaultValueReference.code),
    id: getContextFileNameField(widget.defaultValueReference.id),
    field: { type: Datatype.String },
  };

  const expression: IReferenceExpression = {
    type: ExpressionType.Reference,
    value: filenameReference,
  };
  const references = collectExpressionReferences(expression);
  const paths = references.map(r => mapReferenceToPath(r));

  return {
    paths,
    expression,
    references,
  };
}

type IDefaultReferenceData = ReturnType<typeof getDefaultIdReferenceData>;

/**
 * Ritorna lo schema di yup aggiornato con il metodo `parentWhen`
 * che permette di calcolare il valore di default in base all'espressione.
 *
 * Se l'espressione non è definita, ritorna lo schema originale.
 */
function getDefaultParentWhen(
  schema: yup.StringSchema<Maybe<string | undefined>>,
  data: IDefaultReferenceData,
) {
  if (!data) return schema;

  const { expression, references, paths } = data;

  if (!expression) return schema;

  return schema.parentWhen(paths, (values, schema: yup.AnySchema) => {
    const variables = createEvaluationVariables(references, values);
    const result = evaluateExpression(expression, { variables });

    return result != null ? schema.default(result) : schema;
  });
}
